import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import logo from '../../logo.png'
import { getContext } from 'utils/store'
import { useForgotPasswordMutation } from 'graphql/gen-types'
import {
  Typography,
  Box,
  TextField,
  Button,
  Container,
  Grid,
  Paper,
} from '@mui/material'
import { AlternateEmail, CheckCircleOutline } from '@mui/icons-material'

export function ForgotPasswordInputEmail(): JSX.Element {
  const { setAlertMessage } = getContext()

  // MAIN STATE
  const [email, setEmail] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [forgotPassword] = useForgotPasswordMutation()

  const handleSubmit = async (): Promise<void> => {
    if (!email) {
      setAlertMessage('Invalid email')
      return
    }

    setIsSubmitting(true)
    try {
      await forgotPassword({
        variables: {
          forgotPasswordArgs: {
            email,
          },
        },
      })
      setIsSubmitted(true)
    } catch (error) {
      setAlertMessage(error instanceof Error ? error.message : 'Request failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Container
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
      }}
    >
      <Grid container component={Box} maxWidth={640} rowSpacing={2}>
        <Grid
          item
          xs={9}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <Typography variant="h3">Forgot your password?</Typography>
          <Typography variant="subtitle1">
            Type in your account email
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <img
            style={{ width: '100%', height: 'auto' }}
            src={logo}
            alt="resolvexLogo"
          />
        </Grid>

        <Grid item xs={12}>
          <Grid
            container
            component={Paper}
            elevation={5}
            sx={{
              backgroundColor: (theme) => theme.palette.primary.light,
              p: 2,
            }}
          >
            {isSubmitted ? (
              <>
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    py: 2,
                  }}
                >
                  <CheckCircleOutline color="success" sx={{ fontSize: 48 }} />
                  <Typography variant="h6" align="center">
                    Check your email
                  </Typography>
                  <Typography variant="body1" align="center">
                    If an account exists for {email}, instructions to reset your
                    password were sent to that address.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    component={NavLink}
                    to="/login"
                    variant="contained"
                    color="primary"
                    size="large"
                    id="forgot-password-back-to-login-btn"
                  >
                    Back to Login
                  </Button>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="emailInput"
                    label="Email"
                    variant="outlined"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    InputProps={{
                      sx: {
                        backgroundColor: (theme) =>
                          theme.palette.background.default,
                      },
                      startAdornment: <AlternateEmail />,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    id="forgot-password-submit-btn"
                    variant="contained"
                    color="primary"
                    size="large"
                    type="submit"
                    disabled={isSubmitting}
                    onClick={() => handleSubmit()}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <NavLink to="/login">
                    <Typography variant="subtitle2">Back to Login</Typography>
                  </NavLink>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Container>
  )
}
